'use client';

import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  flexRender,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
  ExpandedState,
  GroupingState,
  ColumnOrderState,
  ColumnSizingState,
  ColumnSizingInfoState,
  Table as TanstackTable,
  Row,
  Column,
} from '@tanstack/table-core';
import { rankItem } from '@tanstack/match-sorter-utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronUp,
  LuChevronDown,
  LuArrowUpDown,
  LuSettings,
  LuDownload,
  LuCopy,
  LuTrash2,
  LuEyeOff,
  LuColumns2,
  LuGripVertical,
  LuEye,
  LuFilter,
  LuCode,
} from 'react-icons/lu';

import {
  Box,
  Button,
  Input,
  HStack,
  VStack,
  Text,
  IconButton,
  Stack,
  Badge,
  Select,
  Popover,
  Checkbox,
  TableRoot,
  TableHeader,
  TableBody,
  TableRow,
  TableColumnHeader,
  TableCell,
  useToast,
} from '../index';
import { memoizeComponent } from '../utils/performance';

import { DataTableSkeleton } from './data-table-skeleton';
import { ColumnTypeIcon, detectColumnType } from './data-table/column-type-icon';
import { SmartCellRenderer } from './data-table/cell-renderers';
import { CodeEditorFilter } from './data-table/code-editor-filter';

// Type augmentation for column meta
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData = unknown, TValue = unknown> {
    filterVariant?: 'text' | 'select' | 'range' | 'date' | 'boolean';
    filterOptions?: Array<{ value: string; label: string }>;
    filterPlaceholder?: string;
    enableOrdering?: boolean;
    // Type-safe accessors using generic types
    accessor?: keyof TData | ((row: TData) => TValue);
    valueFormatter?: (value: TValue) => string;
  }
}

// Types
export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];

  // Core features
  searchPlaceholder?: string;
  isLoading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];

  // Feature flags
  enableRowSelection?: boolean;
  enableMultiSelect?: boolean;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableGlobalFilter?: boolean;
  enableRowExpansion?: boolean;
  enableGrouping?: boolean;
  enableColumnVisibility?: boolean;
  enableExport?: boolean;
  enableStickyHeader?: boolean;
  enablePagination?: boolean;
  enableColumnOrdering?: boolean;
  enableColumnFilters?: boolean;
  showFilterRow?: boolean;

  // Column resizing features
  enableColumnResizing?: boolean;
  columnResizeMode?: 'onChange' | 'onEnd';
  columnResizeDirection?: 'ltr' | 'rtl';
  onColumnSizingChange?: (columnSizing: Record<string, number>) => void;
  onColumnSizingInfoChange?: (info: ColumnSizingInfoState) => void;
  initialColumnSizing?: Record<string, number>;

  // Mobile features
  enableMobileScrollIndicators?: boolean;
  enableTouchGestures?: boolean;
  enableStickyColumns?: boolean;
  stickyColumnIds?: string[];
  enableMobileOptimization?: boolean;

  // Styling
  striped?: boolean;
  highlightOnHover?: boolean;
  variant?: 'simple' | 'striped' | 'unstyled';
  size?: 'sm' | 'md' | 'lg';
  colorPalette?: string;

  // Cell features
  enableCellCopy?: boolean;

  // Advanced features
  enableCodeEditorFilter?: boolean;
  savedFilters?: Array<{
    name: string;
    code: string;
    description: string;
  }>;
  onSaveFilter?: (name: string, code: string, description: string) => void;

  // Callbacks
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  onRowClick?: (row: Row<TData>) => void;
  onRowDoubleClick?: (row: Row<TData>) => void;
  onExport?: (data: TData[]) => void;
  onBulkDelete?: (selectedRows: TData[]) => Promise<void>;
  onBulkCopy?: (selectedRows: TData[]) => void;

  // Custom components
  renderTopToolbar?: (table: TanstackTable<TData>) => React.ReactNode;
  renderBottomToolbar?: (table: TanstackTable<TData>) => React.ReactNode;
  renderRowActions?: (row: Row<TData>) => React.ReactNode;
  renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;

  // Initial states
  initialSorting?: SortingState;
  initialFilters?: ColumnFiltersState;
  initialExpanded?: ExpandedState;
  initialGrouping?: GroupingState;
  initialColumnVisibility?: VisibilityState;
  initialColumnOrder?: ColumnOrderState;

  // Virtual scrolling
  enableVirtualScrolling?: boolean;
  virtualScrollHeight?: number | string;
  estimatedRowHeight?: number;
  overscan?: number;
}

// Fuzzy filter function
const fuzzyFilter = <TData,>(
  row: Row<TData>,
  columnId: string,
  value: string,
  addMeta: (meta: { itemRank: ReturnType<typeof rankItem> }) => void
) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

// Note: Using namespace components directly from imports

// Main DataTable component
function DataTableComponent<TData, TValue>({
  columns,
  data,
  searchPlaceholder = 'Search all columns...',
  isLoading = false,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 50, 100],

  // Features
  enableRowSelection = false,
  enableMultiSelect = true,
  enableSorting = true,
  enableFiltering = true,
  enableGlobalFilter = true,
  enableRowExpansion = false,
  enableGrouping = false,
  enableColumnVisibility = true,
  enableExport = false,
  enableStickyHeader = false,
  enablePagination = true,
  enableColumnOrdering = false,
  enableColumnFilters = false,
  showFilterRow = false,

  // Column resizing
  enableColumnResizing = true,
  columnResizeMode = 'onEnd',
  columnResizeDirection = 'ltr',
  onColumnSizingChange,
  onColumnSizingInfoChange,
  initialColumnSizing = {},

  // Mobile features
  enableMobileScrollIndicators = false,
  enableTouchGestures = false,
  enableStickyColumns = false,
  stickyColumnIds = [],
  enableMobileOptimization = false,

  // Styling
  striped = false,
  highlightOnHover = true,
  variant = 'simple',
  size = 'md',
  colorPalette = 'gray',

  // Callbacks
  onRowSelectionChange,
  onRowClick,
  onRowDoubleClick,
  onExport,
  onBulkDelete,
  onBulkCopy,

  // Custom components
  renderTopToolbar,
  renderBottomToolbar,
  renderRowActions,
  renderExpandedRow,
  renderEmptyState,

  // Initial states
  initialSorting = [],
  initialFilters = [],
  initialExpanded = {},
  initialGrouping = [],
  initialColumnVisibility = {},
  initialColumnOrder = [],

  // Virtual scrolling
  enableVirtualScrolling = false,
  virtualScrollHeight = 600,
  estimatedRowHeight = 50,
  overscan = 5,

  // Cell features
  enableCellCopy = false,

  // Advanced features
  enableCodeEditorFilter = false,
  savedFilters = [],
  onSaveFilter,
}: DataTableProps<TData, TValue>) {
  // State
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialFilters);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [expanded, setExpanded] = React.useState<ExpandedState>(initialExpanded);
  const [grouping, setGrouping] = React.useState<GroupingState>(initialGrouping);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    initialColumnOrder.length > 0 ? initialColumnOrder : []
  );
  const [draggedColumn, setDraggedColumn] = React.useState<string | null>(null);
  const [filterRowVisible, setFilterRowVisible] = React.useState(showFilterRow);

  // Code editor filter state
  const [showCodeEditorFilter, setShowCodeEditorFilter] = React.useState(false);
  const [customFilterFunction, setCustomFilterFunction] = React.useState<
    ((row: TData) => boolean) | null
  >(null);
  const [customFilterDescription, setCustomFilterDescription] = React.useState('');

  // Column sizing state
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(initialColumnSizing);
  const [columnSizingInfo, setColumnSizingInfo] = React.useState<ColumnSizingInfoState>({
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    isResizingColumn: false,
    columnSizingStart: [],
  });

  // Mobile-specific state
  const [isMobileDevice, setIsMobileDevice] = React.useState(false);
  const [touchStartX, setTouchStartX] = React.useState<number | null>(null);
  const [touchStartY, setTouchStartY] = React.useState<number | null>(null);
  const [showLeftIndicator, setShowLeftIndicator] = React.useState(false);
  const [showRightIndicator, setShowRightIndicator] = React.useState(false);
  const [tableContainerRef, setTableContainerRef] = React.useState<HTMLDivElement | null>(null);

  // Virtual scrolling ref
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Toast notifications
  const toast = useToast();

  // Cell copy handler
  const handleCellCopy = React.useCallback(
    (_value: string, _columnId?: string, _rowId?: string) => {
      toast({
        title: 'Copied to clipboard',
        description: 'Cell content has been copied successfully',
        status: 'success',
        duration: 2000,
      });
    },
    [toast]
  );

  // Code editor filter handlers
  const handleApplyCustomFilter = React.useCallback(
    (filterFunction: (row: TData) => boolean, description: string) => {
      setCustomFilterFunction(() => filterFunction);
      setCustomFilterDescription(description);
      setShowCodeEditorFilter(false);

      toast({
        title: 'Custom filter applied',
        description: `"${description}" filter is now active`,
        status: 'success',
        duration: 3000,
      });
    },
    [toast]
  );

  const handleSaveCustomFilter = React.useCallback(
    (name: string, code: string, description: string) => {
      if (onSaveFilter) {
        onSaveFilter(name, code, description);
        toast({
          title: 'Filter saved',
          description: `"${name}" has been saved to your filters`,
          status: 'success',
          duration: 2000,
        });
      }
    },
    [onSaveFilter, toast]
  );

  const handleClearCustomFilter = React.useCallback(() => {
    setCustomFilterFunction(null);
    setCustomFilterDescription('');

    toast({
      title: 'Custom filter cleared',
      description: 'Custom filter has been removed',
      status: 'info',
      duration: 2000,
    });
  }, [toast]);

  // Auto-enable virtual scrolling for large datasets
  const shouldEnableVirtualScrolling = React.useMemo(() => {
    return enableVirtualScrolling || data.length > 100;
  }, [enableVirtualScrolling, data.length]);

  // Remove the safe setters and use the state setters directly
  // The issue is likely coming from TanStack Table's internal state management
  // not from our component's state updates

  // Namespace components are already imported

  // Apply custom filter function if present
  const filteredData = React.useMemo(() => {
    if (!customFilterFunction) {
      return data;
    }

    try {
      return data.filter((row, _index) => {
        return customFilterFunction(row);
      });
    } catch {
      // If custom filter fails, return original data
      return data;
    }
  }, [data, customFilterFunction]);

  // Use the filtered data when custom filter is active
  const displayData = React.useMemo(() => {
    return customFilterFunction ? filteredData : data;
  }, [data, filteredData, customFilterFunction]);

  // Table instance - use lazy initial state to prevent mount issues
  const table = useReactTable<TData>({
    data: displayData,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
      grouping,
      globalFilter,
      columnOrder,
      columnSizing,
      columnSizingInfo,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onGroupingChange: setGrouping,
    onGlobalFilterChange: setGlobalFilter,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: (updaterOrValue) => {
      const newValue =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnSizing) : updaterOrValue;
      if (onColumnSizingChange) {
        onColumnSizingChange(newValue);
      } else {
        setColumnSizing(newValue);
      }
    },
    onColumnSizingInfoChange: (updaterOrValue) => {
      const newValue =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnSizingInfo) : updaterOrValue;
      if (onColumnSizingInfoChange) {
        onColumnSizingInfoChange(newValue);
      } else {
        setColumnSizingInfo(newValue);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: enableRowExpansion ? getExpandedRowModel() : undefined,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    globalFilterFn: fuzzyFilter,
    enableRowSelection,
    enableMultiRowSelection: enableMultiSelect,
    getRowCanExpand: enableRowExpansion && renderExpandedRow ? () => true : undefined,
    enableColumnResizing,
    columnResizeMode,
    columnResizeDirection,
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  // Use the appropriate table instance (now uses displayData)
  const displayTable = table;

  // Get table rows for virtual scrolling
  const { rows } = displayTable.getRowModel();

  // Virtual scrolling setup
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: React.useCallback(() => estimatedRowHeight, [estimatedRowHeight]),
    overscan,
    enabled: shouldEnableVirtualScrolling,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Calculate padding for virtual scrolling
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - (virtualItems[virtualItems.length - 1]?.end || 0) : 0;

  // Note: hasActiveFilters is already defined below

  // Handlers
  const handleExport = () => {
    if (onExport) {
      const exportData =
        displayTable.getFilteredSelectedRowModel().rows.length > 0
          ? displayTable.getFilteredSelectedRowModel().rows.map((row) => row.original as TData)
          : displayTable.getFilteredRowModel().rows.map((row) => row.original as TData);
      onExport(exportData);
    }
  };

  const handleClearFilters = () => {
    setColumnFilters([]);
    setGlobalFilter('');
    handleClearCustomFilter();
  };

  // Bulk action handlers
  const handleBulkCopy = async () => {
    try {
      const selectedRows = displayTable
        .getFilteredSelectedRowModel()
        .rows.map((row) => row.original as TData);

      if (selectedRows.length === 0) {
        toast({
          title: 'No rows selected',
          description: 'Please select rows to copy',
          status: 'warning',
          duration: 3000,
        });
        return;
      }

      // Custom copy handler or default CSV format
      if (onBulkCopy) {
        onBulkCopy(selectedRows);
      } else {
        // Default: Convert to CSV and copy to clipboard
        const headers = table
          .getAllColumns()
          .filter(
            (col) =>
              col.getIsVisible() &&
              col.id !== 'select' &&
              col.id !== 'actions' &&
              col.id !== 'expander'
          )
          .map((col) => (typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id));

        const rows = selectedRows.map((row: TData) =>
          headers.map((header) => {
            const col = table
              .getAllColumns()
              .find(
                (c) =>
                  (typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id) === header
              );
            if (col) {
              const columnDef: Record<string, unknown> = col.columnDef as unknown as Record<
                string,
                unknown
              >;
              const accessorKey = columnDef.accessorKey;
              if (accessorKey && typeof accessorKey === 'string') {
                const value = (row as Record<string, unknown>)[accessorKey];
                return value !== undefined && value !== null ? String(value) : '';
              }
            }
            return '';
          })
        );

        const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

        // Copy to clipboard
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(csvContent);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = csvContent;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      }

      toast({
        title: 'Copied successfully',
        description: `${selectedRows.length} row${selectedRows.length > 1 ? 's' : ''} copied to clipboard`,
        status: 'success',
        duration: 3000,
      });
    } catch {
      // Silent error handling for clipboard operations
      toast({
        title: 'Copy failed',
        description: 'Failed to copy rows to clipboard',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleBulkDelete = async () => {
    try {
      const selectedRows = table
        .getFilteredSelectedRowModel()
        .rows.map((row) => row.original as TData);

      if (selectedRows.length === 0) {
        toast({
          title: 'No rows selected',
          description: 'Please select rows to delete',
          status: 'warning',
          duration: 3000,
        });
        return;
      }

      if (!onBulkDelete) {
        toast({
          title: 'Delete not available',
          description: 'Bulk delete functionality is not configured',
          status: 'warning',
          duration: 3000,
        });
        return;
      }

      // Simple confirmation (we'll enhance this with a proper modal later)
      const confirmed = window.confirm(
        `Are you sure you want to delete ${selectedRows.length} row${selectedRows.length > 1 ? 's' : ''}?`
      );

      if (!confirmed) return;

      await onBulkDelete(selectedRows);

      // Clear selection after successful delete
      setRowSelection({});

      toast({
        title: 'Deleted successfully',
        description: `${selectedRows.length} row${selectedRows.length > 1 ? 's' : ''} deleted`,
        status: 'success',
        duration: 3000,
      });
    } catch {
      // Silent error handling for delete operations
      toast({
        title: 'Delete failed',
        description: 'Failed to delete rows',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const hasActiveFilters = columnFilters.length > 0 || globalFilter || customFilterFunction;
  const selectedRowCount = Object.keys(rowSelection).length;

  // Column drag and drop handlers
  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    if (!enableColumnOrdering) return;
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!enableColumnOrdering) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    if (!enableColumnOrdering) return;
    e.preventDefault();

    const sourceColumnId = e.dataTransfer.getData('text/plain');
    if (sourceColumnId === targetColumnId) return;

    const newColumnOrder = [...columnOrder];
    const sourceIndex = newColumnOrder.indexOf(sourceColumnId);
    const targetIndex = newColumnOrder.indexOf(targetColumnId);

    if (sourceIndex !== -1 && targetIndex !== -1) {
      newColumnOrder.splice(sourceIndex, 1);
      newColumnOrder.splice(targetIndex, 0, sourceColumnId);
      setColumnOrder(newColumnOrder);
    }
  };

  React.useEffect(() => {
    onRowSelectionChange?.(rowSelection);
  }, [rowSelection, onRowSelectionChange]);

  // Mobile device detection
  React.useEffect(() => {
    if (!enableMobileOptimization) return;

    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobileDevice(isMobile || isTouch);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [enableMobileOptimization]);

  // Touch gesture handlers
  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent) => {
      if (!enableTouchGestures) return;

      const touch = e.touches[0];
      setTouchStartX(touch.clientX);
      setTouchStartY(touch.clientY);
    },
    [enableTouchGestures]
  );

  const handleTouchMove = React.useCallback(
    (e: React.TouchEvent) => {
      if (!enableTouchGestures || touchStartX === null || touchStartY === null) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartX);
      const deltaY = Math.abs(touch.clientY - touchStartY);

      // If horizontal swipe is dominant, prevent vertical scroll
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    },
    [enableTouchGestures, touchStartX, touchStartY]
  );

  const handleTouchEnd = React.useCallback(() => {
    if (!enableTouchGestures) return;

    setTouchStartX(null);
    setTouchStartY(null);
  }, [enableTouchGestures]);

  // Scroll indicators update
  const updateScrollIndicators = React.useCallback(() => {
    if (!enableMobileScrollIndicators || !tableContainerRef) return;

    const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef;
    setShowLeftIndicator(scrollLeft > 0);
    setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 1);
  }, [enableMobileScrollIndicators, tableContainerRef]);

  React.useEffect(() => {
    if (!enableMobileScrollIndicators || !tableContainerRef) return;

    const handleScroll = () => updateScrollIndicators();
    const resizeObserver = new ResizeObserver(updateScrollIndicators);

    tableContainerRef.addEventListener('scroll', handleScroll, { passive: true });
    resizeObserver.observe(tableContainerRef);
    updateScrollIndicators();

    return () => {
      tableContainerRef.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [enableMobileScrollIndicators, tableContainerRef, updateScrollIndicators]);

  // Screen reader announcements
  const [announcement, setAnnouncement] = React.useState('');

  React.useEffect(() => {
    if (selectedRowCount > 0) {
      setAnnouncement(`${selectedRowCount} row${selectedRowCount > 1 ? 's' : ''} selected`);
    }
  }, [selectedRowCount]);

  React.useEffect(() => {
    const sortedColumn = sorting[0];
    if (sortedColumn) {
      const column = displayTable.getColumn(sortedColumn.id);
      if (column) {
        const direction = sortedColumn.desc ? 'descending' : 'ascending';
        setAnnouncement(`Table sorted by ${getColumnLabel(column)} ${direction}`);
      }
    }
  }, [sorting, displayTable]);

  const getColumnLabel = <TData,>(col: Column<TData, unknown>) =>
    typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;

  // Show skeleton when loading
  if (isLoading) {
    return (
      <DataTableSkeleton
        rows={pageSize}
        columns={columns.length}
        showCheckbox={enableRowSelection}
        showActions={!!renderRowActions}
        showToolbar={!renderTopToolbar}
        showPagination={enablePagination}
      />
    );
  }

  return (
    <Stack gap={3}>
      {/* Screen reader announcements */}
      <Box
        position="absolute"
        width="1px"
        height="1px"
        padding={0}
        margin="-1px"
        overflow="hidden"
        clip="rect(0, 0, 0, 0)"
        whiteSpace="nowrap"
        border="0"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </Box>

      {/* Top Toolbar */}
      <VStack align="stretch" gap={2}>
        {renderTopToolbar ? (
          renderTopToolbar(table)
        ) : (
          <>
            {/* Search and actions */}
            <HStack justifyContent="space-between" flexWrap="wrap" gap={3}>
              <HStack gap={2} flex="1" maxWidth="md">
                {enableGlobalFilter && (
                  <Input
                    placeholder={searchPlaceholder}
                    value={globalFilter ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setGlobalFilter(e.target.value)
                    }
                    aria-label="Search table"
                  />
                )}
                {enableCodeEditorFilter && (
                  <Button
                    size={size}
                    variant={customFilterFunction ? 'primary' : 'outline'}
                    colorPalette="blue"
                    leftIcon={<LuCode />}
                    onClick={() => setShowCodeEditorFilter(true)}
                  >
                    Code Filter
                  </Button>
                )}
              </HStack>

              <HStack gap={2}>
                {selectedRowCount > 0 && <Badge>{selectedRowCount} selected</Badge>}

                {customFilterFunction && (
                  <HStack gap={1} alignItems="center">
                    <Badge colorPalette="blue">
                      <LuCode size={12} />
                      Custom Filter
                    </Badge>
                    <Text fontSize="xs" color="#6B7280">
                      {customFilterDescription}
                    </Text>
                  </HStack>
                )}

                {hasActiveFilters && (
                  <Button size={size} variant="ghost" onClick={handleClearFilters}>
                    <LuTrash2 /> Clear filters
                  </Button>
                )}

                {enableExport && (
                  <IconButton aria-label="Export" icon={<LuDownload />} onClick={handleExport} />
                )}

                {enableColumnFilters && (
                  <Box position="relative">
                    <IconButton
                      aria-label="Toggle filters"
                      icon={<LuFilter />}
                      size="sm"
                      variant={filterRowVisible ? 'primary' : 'ghost'}
                      colorPalette={filterRowVisible ? colorPalette : undefined}
                      onClick={() => setFilterRowVisible(!filterRowVisible)}
                    />
                    {columnFilters.length > 0 && !filterRowVisible && (
                      <Box
                        position="absolute"
                        top="-2px"
                        right="-2px"
                        width="12px"
                        height="12px"
                        borderRadius="full"
                        backgroundColor="#EF4444"
                        animation="pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
                      />
                    )}
                  </Box>
                )}

                {enableColumnVisibility && (
                  <Popover.Root>
                    <Popover.Trigger asChild>
                      <IconButton
                        aria-label="View columns"
                        icon={<LuColumns2 />}
                        size="sm"
                        variant="ghost"
                      />
                    </Popover.Trigger>
                    <Popover.Positioner>
                      <Popover.Content>
                        <VStack align="stretch" padding="16px" gap="12px">
                          <HStack justifyContent="space-between" marginBottom="8px">
                            <Text fontWeight="semibold" fontSize="md" color="#1F2937">
                              Toggle Columns
                            </Text>
                            <IconButton
                              aria-label="Reset columns"
                              icon={<LuTrash2 />}
                              size="xs"
                              variant="ghost"
                              onClick={() => displayTable.resetColumnVisibility()}
                            />
                          </HStack>

                          <Box height="1px" backgroundColor="#E5E7EB" marginY="4px" />

                          <VStack align="stretch" gap="8px">
                            {table
                              .getAllColumns()
                              .filter((column) => column.getCanHide())
                              .map((column) => {
                                const isVisible = column.getIsVisible();
                                return (
                                  <Box
                                    key={column.id}
                                    padding="8px"
                                    borderRadius="md"
                                    _hover={{ backgroundColor: '#F9FAFB' }}
                                    transition="background-color 0.2s"
                                  >
                                    <Checkbox
                                      checked={isVisible}
                                      onCheckedChange={(details) => {
                                        const visible =
                                          typeof details.checked === 'boolean'
                                            ? details.checked
                                            : true;
                                        column.toggleVisibility(visible);
                                      }}
                                    >
                                      <HStack justifyContent="space-between" width="full">
                                        <Text
                                          fontSize="sm"
                                          color={isVisible ? '#1F2937' : '#6B7280'}
                                          fontWeight={isVisible ? 'medium' : 'normal'}
                                        >
                                          {getColumnLabel(column)}
                                        </Text>
                                        {isVisible && <LuEye size={14} color="#10B981" />}
                                      </HStack>
                                    </Checkbox>
                                  </Box>
                                );
                              })}
                          </VStack>

                          <Box height="1px" backgroundColor="#E5E7EB" marginY="4px" />

                          <HStack gap="8px">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                displayTable.getAllColumns().forEach((column) => {
                                  if (column.getCanHide()) {
                                    column.toggleVisibility(true);
                                  }
                                });
                              }}
                            >
                              Show All
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                displayTable.getAllColumns().forEach((column) => {
                                  if (column.getCanHide() && column.id !== 'select') {
                                    column.toggleVisibility(false);
                                  }
                                });
                              }}
                            >
                              Hide All
                            </Button>
                          </HStack>
                        </VStack>
                      </Popover.Content>
                    </Popover.Positioner>
                  </Popover.Root>
                )}
              </HStack>
            </HStack>

            {/* Active groupings display */}
            {enableGrouping && grouping.length > 0 && (
              <HStack gap={2} wrap="wrap">
                <Text fontSize="sm" color="#4B5563">
                  Grouped by:
                </Text>
                {grouping.map((columnId) => {
                  const column = displayTable.getColumn(columnId);
                  return column ? (
                    <HStack key={columnId} gap="4px">
                      <Badge>{getColumnLabel(column)}</Badge>
                      <IconButton
                        aria-label="Remove grouping"
                        icon={<LuTrash2 />}
                        size="xs"
                        variant="ghost"
                        onClick={() => column.toggleGrouping()}
                      />
                    </HStack>
                  ) : null;
                })}
                <Button size="sm" variant="ghost" onClick={() => displayTable.resetGrouping()}>
                  Clear all
                </Button>
              </HStack>
            )}

            {/* Selected row actions */}
            {selectedRowCount > 0 && (
              <HStack
                padding={2}
                backgroundColor={`${colorPalette}.50`}
                borderRadius="md"
                justifyContent="space-between"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {selectedRowCount} row{selectedRowCount > 1 ? 's' : ''} selected
                </Text>
                <HStack>
                  <Button size="sm" variant="ghost" onClick={handleBulkCopy}>
                    <LuCopy /> Copy
                  </Button>
                  <Button size="sm" variant="ghost" colorPalette="red" onClick={handleBulkDelete}>
                    <LuTrash2 /> Delete
                  </Button>
                </HStack>
              </HStack>
            )}
          </>
        )}
      </VStack>

      {/* Table */}
      <Box
        ref={setTableContainerRef}
        overflowX="auto"
        border="1px solid"
        borderColor="#E5E7EB"
        borderRadius="md"
        position="relative"
        role="region"
        aria-label="Data table"
        tabIndex={0}
        boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
        transition="box-shadow 0.3s ease"
        _hover={{
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          ...(enableMobileOptimization &&
            isMobileDevice && {
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
              scrollSnapType: 'x proximity',
              transform: 'translateZ(0)',
              willChange: 'scroll-position',
            }),
        }}
      >
        {/* Scroll Indicators */}
        {enableMobileScrollIndicators && isMobileDevice && showLeftIndicator && (
          <Box
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            zIndex="20"
            width="24px"
            height="48px"
            background="linear-gradient(to right, rgba(255,255,255,0.9), transparent)"
            borderRadius="0 8px 8px 0"
            boxShadow="2px 0 8px rgba(0,0,0,0.1)"
            pointerEvents="none"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <LuChevronLeft size={16} color="#6B7280" />
          </Box>
        )}

        {enableMobileScrollIndicators && isMobileDevice && showRightIndicator && (
          <Box
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            zIndex="20"
            width="24px"
            height="48px"
            background="linear-gradient(to left, rgba(255,255,255,0.9), transparent)"
            borderRadius="8px 0 0 8px"
            boxShadow="-2px 0 8px rgba(0,0,0,0.1)"
            pointerEvents="none"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <LuChevronRight size={16} color="#6B7280" />
          </Box>
        )}
        <TableRoot
          variant={variant === 'simple' ? 'outline' : variant === 'striped' ? 'striped' : 'outline'}
          size={size}
          role="table"
        >
          <TableHeader
            position={enableStickyHeader ? 'sticky' : 'relative'}
            top={enableStickyHeader ? '0' : undefined}
            zIndex={enableStickyHeader ? 10 : undefined}
            backgroundColor="white"
          >
            {displayTable.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { column } = header;
                  return (
                    <TableColumnHeader
                      key={header.id}
                      draggable={enableColumnOrdering && !header.isPlaceholder}
                      onDragStart={(e: React.DragEvent) => handleDragStart(e, column.id)}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                      onDrop={(e: React.DragEvent) => handleDrop(e, column.id)}
                      style={{
                        width: header.getSize(),
                        position: 'relative',
                        cursor: enableColumnOrdering ? 'move' : 'default',
                        opacity: draggedColumn === column.id ? 0.5 : 1,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: draggedColumn === column.id ? 'scale(0.95)' : 'scale(1)',
                        ...(enableStickyColumns &&
                          stickyColumnIds.includes(column.id) && {
                            position: 'sticky',
                            left: 0,
                            zIndex: 15,
                            backgroundColor: 'white',
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                          }),
                      }}
                      _hover={{
                        backgroundColor: '#F9FAFB',
                        '&:not([data-sticky="true"])': {
                          zIndex: 10,
                        },
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <HStack justifyContent="space-between">
                          <HStack gap={1}>
                            {enableColumnOrdering && (
                              <Box
                                opacity={0.3}
                                _hover={{ opacity: 0.8 }}
                                cursor="grab"
                                padding="4px"
                                transition="all 0.2s ease"
                                _active={{ cursor: 'grabbing', transform: 'scale(0.9)' }}
                              >
                                <LuGripVertical size={14} />
                              </Box>
                            )}
                            {/* Column type icon for better visual recognition */}
                            {data.length > 0 && (
                              <ColumnTypeIcon
                                type={detectColumnType(
                                  data[0][column.id as keyof (typeof data)[0]]
                                )}
                                showTooltip={true}
                              />
                            )}
                            {enableGrouping && column.getCanGroup() && (
                              <IconButton
                                aria-label="Group by column"
                                icon={<LuGripVertical />}
                                size="xs"
                                variant={column.getIsGrouped() ? 'primary' : 'ghost'}
                                colorPalette={column.getIsGrouped() ? colorPalette : 'gray'}
                                onClick={() => column.toggleGrouping()}
                              />
                            )}
                            <Box>{flexRender(column.columnDef.header, header.getContext())}</Box>
                          </HStack>
                          {enableSorting && column.getCanSort() && (
                            <Box
                              as="button"
                              padding="4px"
                              borderRadius="md"
                              _hover={{ backgroundColor: '#F3F4F6' }}
                              transition="all 0.2s ease"
                              onClick={column.getToggleSortingHandler()}
                              aria-label="Sort"
                            >
                              <Box
                                transition="transform 0.3s ease"
                                transform={
                                  column.getIsSorted() === 'desc'
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)'
                                }
                              >
                                {column.getIsSorted() ? (
                                  <LuChevronUp
                                    size={16}
                                    color={column.getIsSorted() ? '#3B82F6' : '#6B7280'}
                                  />
                                ) : (
                                  <LuArrowUpDown size={16} color="#6B7280" />
                                )}
                              </Box>
                            </Box>
                          )}
                        </HStack>
                      )}
                      {/* Column resize handle */}
                      {enableColumnResizing && header.column.getCanResize() && (
                        <Box
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className="column-resizer"
                          position="absolute"
                          right="0"
                          top="0"
                          height="100%"
                          width="5px"
                          background={header.column.getIsResizing() ? '#3B82F6' : 'transparent'}
                          cursor="col-resize"
                          userSelect="none"
                          touchAction="none"
                          _hover={{
                            background: '#3B82F6',
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </TableColumnHeader>
                  );
                })}
              </TableRow>
            ))}
            {/* Filter Row */}
            {enableColumnFilters && (
              <TableRow className={filterRowVisible ? 'filter-row-visible' : 'filter-row-hidden'}>
                {displayTable.getAllColumns().map((column) => {
                  if (
                    column.id === 'select' ||
                    column.id === 'actions' ||
                    column.id === 'expander'
                  ) {
                    return (
                      <TableColumnHeader
                        key={column.id}
                        style={{
                          ...(enableStickyColumns &&
                            stickyColumnIds.includes(column.id) && {
                              position: 'sticky',
                              left: 0,
                              zIndex: 15,
                              backgroundColor: 'white',
                              boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                            }),
                        }}
                      />
                    );
                  }

                  const filterValue = column.getFilterValue();
                  const columnFilterMeta = column.columnDef.meta;
                  const filterVariant = columnFilterMeta?.filterVariant || 'text';

                  return (
                    <TableColumnHeader
                      key={column.id}
                      padding="8px"
                      style={{
                        ...(enableStickyColumns &&
                          stickyColumnIds.includes(column.id) && {
                            position: 'sticky',
                            left: 0,
                            zIndex: 15,
                            backgroundColor: 'white',
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                          }),
                      }}
                    >
                      {column.getCanFilter() ? (
                        <Box width="full">
                          {filterVariant === 'text' && (
                            <Input
                              placeholder={`Filter ${getColumnLabel(column)}...`}
                              value={(filterValue ?? '') as string}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                column.setFilterValue(e.target.value)
                              }
                            />
                          )}
                          {filterVariant === 'select' && (
                            <Select
                              selectSize="sm"
                              placeholder="All"
                              value={(filterValue ?? '') as string}
                              onChange={(value) => column.setFilterValue(value || undefined)}
                              options={
                                (
                                  columnFilterMeta as {
                                    filterOptions?: Array<{ value: string; label: string }>;
                                  }
                                )?.filterOptions ||
                                Array.from(
                                  new Set(
                                    table
                                      .getCoreRowModel()
                                      .flatRows.map((row) => row.getValue(column.id) as string)
                                  )
                                ).map((value) => ({ value, label: value }))
                              }
                            />
                          )}
                          {filterVariant === 'range' && (
                            <HStack gap="4px">
                              <Input
                                type="number"
                                placeholder="Min"
                                value={(filterValue as [number, number])?.[0] ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  column.setFilterValue((old: [number, number]) => [
                                    val ? Number(val) : undefined,
                                    old?.[1],
                                  ]);
                                }}
                              />
                              <Input
                                type="number"
                                placeholder="Max"
                                value={(filterValue as [number, number])?.[1] ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  column.setFilterValue((old: [number, number]) => [
                                    old?.[0],
                                    val ? Number(val) : undefined,
                                  ]);
                                }}
                              />
                            </HStack>
                          )}
                        </Box>
                      ) : null}
                    </TableColumnHeader>
                  );
                })}
              </TableRow>
            )}
          </TableHeader>
          {shouldEnableVirtualScrolling ? (
            // Virtual scrolling container
            <Box
              ref={scrollContainerRef}
              height={
                typeof virtualScrollHeight === 'number'
                  ? `${virtualScrollHeight}px`
                  : virtualScrollHeight
              }
              overflowY="auto"
              position="relative"
            >
              {rows.length === 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center" height="200px">
                  {renderEmptyState ? (
                    renderEmptyState()
                  ) : (
                    <VStack gap={4} paddingY={8}>
                      <LuEyeOff size={48} color="#D1D5DB" />
                      <Text color="#6B7280" fontSize="lg">
                        No results found
                      </Text>
                      {hasActiveFilters && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette={colorPalette}
                          onClick={handleClearFilters}
                        >
                          Clear filters
                        </Button>
                      )}
                    </VStack>
                  )}
                </Box>
              ) : (
                <Box position="relative" height={`${totalSize}px`}>
                  {/* Top padding */}
                  {paddingTop > 0 && <Box height={`${paddingTop}px`} />}

                  {/* Virtual rows */}
                  <TableRoot
                    variant={
                      variant === 'simple'
                        ? 'outline'
                        : variant === 'striped'
                          ? 'striped'
                          : 'outline'
                    }
                    size={size}
                  >
                    <TableBody>
                      {virtualItems.map((virtualRow) => {
                        const row = rows[virtualRow.index];
                        return (
                          <React.Fragment key={row.id}>
                            <TableRow
                              data-state={row.getIsSelected() && 'selected'}
                              style={{
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start - paddingTop}px)`,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                              }}
                              onClick={() => onRowClick?.(row)}
                              onDoubleClick={() => onRowDoubleClick?.(row)}
                              onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onRowClick?.(row);
                                }
                              }}
                              backgroundColor={
                                striped && row.index % 2
                                  ? colorPalette === 'gray'
                                    ? '#F9FAFB'
                                    : `${colorPalette}.50`
                                  : undefined
                              }
                              cursor={onRowClick ? 'pointer' : 'default'}
                              transition="all 0.2s ease"
                              _hover={{
                                backgroundColor:
                                  striped && row.index % 2
                                    ? colorPalette === 'gray'
                                      ? '#F3F4F6'
                                      : `${colorPalette}.100`
                                    : '#F9FAFB',
                                transform: highlightOnHover
                                  ? 'translateY(-1px)'
                                  : 'translateX(2px)',
                                boxShadow: highlightOnHover
                                  ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                                  : '0 1px 3px rgba(0, 0, 0, 0.1)',
                              }}
                              _active={{
                                transform: 'translateY(0)',
                                boxShadow: 'none',
                              }}
                              tabIndex={onRowClick ? 0 : undefined}
                              role="row"
                              aria-selected={row.getIsSelected()}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  style={{
                                    width: cell.column.getSize(),
                                    ...(enableStickyColumns &&
                                      stickyColumnIds.includes(cell.column.id) && {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 10,
                                        backgroundColor:
                                          striped && row.index % 2
                                            ? colorPalette === 'gray'
                                              ? '#F9FAFB'
                                              : `var(--chakra-colors-${colorPalette}-50)`
                                            : 'white',
                                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                      }),
                                  }}
                                >
                                  {cell.getIsGrouped() ? (
                                    // If it's a grouped cell, render the aggregated/grouped value
                                    <HStack gap={2}>
                                      <IconButton
                                        aria-label="Expand group"
                                        icon={
                                          row.getIsExpanded() ? (
                                            <LuChevronDown />
                                          ) : (
                                            <LuChevronRight />
                                          )
                                        }
                                        size="xs"
                                        variant="ghost"
                                        onClick={row.getToggleExpandedHandler()}
                                      />
                                      <Text fontWeight="medium">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}{' '}
                                        ({row.subRows.length})
                                      </Text>
                                    </HStack>
                                  ) : cell.getIsAggregated() ? (
                                    // If it's an aggregated cell, render the aggregated value
                                    flexRender(
                                      cell.column.columnDef.aggregatedCell ??
                                        cell.column.columnDef.cell,
                                      cell.getContext()
                                    )
                                  ) : cell.getIsPlaceholder() ? null : ( // For cells that are not grouped or aggregated
                                    <SmartCellRenderer
                                      value={cell.getValue()}
                                      columnId={cell.column.id}
                                      rowId={row.id}
                                      enableCellCopy={enableCellCopy}
                                      onCellCopy={handleCellCopy}
                                      context={cell.getContext()}
                                      cell={cell}
                                    />
                                  )}
                                </TableCell>
                              ))}
                              {renderRowActions && <TableCell>{renderRowActions(row)}</TableCell>}
                            </TableRow>
                            {row.getIsExpanded() && renderExpandedRow && (
                              <TableRow>
                                <TableCell>{renderExpandedRow(row)}</TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </TableRoot>

                  {/* Bottom padding */}
                  {paddingBottom > 0 && <Box height={`${paddingBottom}px`} />}
                </Box>
              )}
            </Box>
          ) : (
            // Regular table body
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      data-state={row.getIsSelected() && 'selected'}
                      onClick={() => onRowClick?.(row)}
                      onDoubleClick={() => onRowDoubleClick?.(row)}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick?.(row);
                        }
                      }}
                      _hover={
                        highlightOnHover
                          ? {
                              backgroundColor:
                                colorPalette === 'gray' ? '#F9FAFB' : `${colorPalette}.50`,
                              transform: 'translateX(2px)',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            }
                          : undefined
                      }
                      backgroundColor={
                        striped && row.index % 2
                          ? colorPalette === 'gray'
                            ? '#F9FAFB'
                            : `${colorPalette}.50`
                          : undefined
                      }
                      cursor={onRowClick ? 'pointer' : 'default'}
                      transition="all 0.2s ease"
                      tabIndex={onRowClick ? 0 : undefined}
                      role="row"
                      aria-selected={row.getIsSelected()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            ...(enableStickyColumns &&
                              stickyColumnIds.includes(cell.column.id) && {
                                position: 'sticky',
                                left: 0,
                                zIndex: 10,
                                backgroundColor:
                                  striped && row.index % 2
                                    ? colorPalette === 'gray'
                                      ? '#F9FAFB'
                                      : `var(--chakra-colors-${colorPalette}-50)`
                                    : 'white',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                              }),
                          }}
                        >
                          {cell.getIsGrouped() ? (
                            // If it's a grouped cell, render the aggregated/grouped value
                            <HStack gap={2}>
                              <IconButton
                                aria-label="Expand group"
                                icon={row.getIsExpanded() ? <LuChevronDown /> : <LuChevronRight />}
                                size="xs"
                                variant="ghost"
                                onClick={row.getToggleExpandedHandler()}
                              />
                              <Text fontWeight="medium">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())} (
                                {row.subRows.length})
                              </Text>
                            </HStack>
                          ) : cell.getIsAggregated() ? (
                            // If it's an aggregated cell, render the aggregated value
                            flexRender(
                              cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          ) : cell.getIsPlaceholder() ? null : ( // For cells that are not grouped or aggregated
                            <SmartCellRenderer
                              value={cell.getValue()}
                              columnId={cell.column.id}
                              rowId={row.id}
                              enableCellCopy={enableCellCopy}
                              onCellCopy={handleCellCopy}
                              context={cell.getContext()}
                              cell={cell}
                            />
                          )}
                        </TableCell>
                      ))}
                      {renderRowActions && <TableCell>{renderRowActions(row)}</TableCell>}
                    </TableRow>
                    {row.getIsExpanded() && renderExpandedRow && (
                      <TableRow>
                        <TableCell>{renderExpandedRow(row)}</TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell textAlign="center">
                    {renderEmptyState ? (
                      renderEmptyState()
                    ) : (
                      <VStack gap={4} paddingY={8}>
                        <LuEyeOff size={48} color="#D1D5DB" />
                        <Text color="#6B7280" fontSize="lg">
                          No results found
                        </Text>
                        {hasActiveFilters && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorPalette={colorPalette}
                            onClick={handleClearFilters}
                          >
                            Clear filters
                          </Button>
                        )}
                      </VStack>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </TableRoot>

        {/* Column resize indicator */}
        {table.getState().columnSizingInfo.isResizingColumn && (
          <Box
            position="absolute"
            top="0"
            left={`${table.getState().columnSizingInfo.startOffset}px`}
            transform={`translateX(${table.getState().columnSizingInfo.deltaOffset}px)`}
            height="100%"
            width="2px"
            background="#3B82F6"
            zIndex={1000}
            pointerEvents="none"
            transition="none"
          />
        )}
      </Box>

      {/* Pagination */}
      {renderBottomToolbar ? (
        renderBottomToolbar(table)
      ) : enablePagination ? (
        <HStack justifyContent="space-between" align="center">
          <HStack gap={2}>
            <Text fontSize="sm" color="#4B5563">
              Showing {table.getState().pagination.pageIndex * pageSize + 1} to{' '}
              {Math.min((table.getState().pagination.pageIndex + 1) * pageSize, data.length)} of{' '}
              {data.length} results
            </Text>
            {enableRowSelection && (
              <Text fontSize="sm" color="#4B5563">
                ({table.getFilteredSelectedRowModel().rows.length} selected)
              </Text>
            )}
          </HStack>

          <HStack gap={2}>
            <HStack gap={1}>
              <IconButton
                aria-label="First page"
                icon={<LuChevronLeft />}
                size={size}
                variant="outline"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                aria-label="Previous page"
                icon={<LuChevronLeft />}
                size={size}
                variant="outline"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <HStack gap={1} paddingX={3}>
                <Text fontSize="sm">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </Text>
              </HStack>
              <IconButton
                aria-label="Next page"
                icon={<LuChevronRight />}
                size={size}
                variant="outline"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
              <IconButton
                aria-label="Last page"
                icon={<LuChevronRight />}
                size={size}
                variant="outline"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              />
            </HStack>

            <Select
              value={table.getState().pagination.pageSize.toString()}
              onChange={(value) => {
                table.setPageSize(Number(value));
              }}
              selectSize={size}
              containerClassName="w-[120px]"
              options={pageSizeOptions.map((size) => ({
                value: size.toString(),
                label: `Show ${size}`,
              }))}
            />
          </HStack>
        </HStack>
      ) : null}

      {/* Code Editor Filter */}
      {enableCodeEditorFilter && showCodeEditorFilter && (
        <CodeEditorFilter
          columns={columns.map((col) => {
            const columnDef = col as ColumnDef<TData, TValue> & {
              accessorKey?: string;
              accessorFn?: (row: TData) => unknown;
            };
            return {
              id: col.id || '',
              header: (typeof col.header === 'string' ? col.header : col.id) || '',
              accessorKey: columnDef.accessorKey,
              accessorFn: columnDef.accessorFn
                ? (row: unknown) => columnDef.accessorFn!(row as TData)
                : undefined,
            };
          })}
          onApplyFilter={handleApplyCustomFilter}
          onClose={() => setShowCodeEditorFilter(false)}
          savedFilters={savedFilters}
          onSaveFilter={handleSaveCustomFilter}
        />
      )}
    </Stack>
  );
}

// Export DataTable without memoization to prevent mount issues
export const DataTable = memoizeComponent(DataTableComponent) as typeof DataTableComponent;

// Add global styles for animations
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideUp {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-10px);
      }
    }
    
    @keyframes fadeInScale {
      from {
        opacity: 0;
        transform: scale(0.8);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }

    .filter-row-visible {
      display: table-row !important;
      animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .filter-row-hidden {
      display: none !important;
      animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
  `;
  document.head.appendChild(style);
}

// Column helper functions
export function createSortableColumn<TData>(
  config: Partial<ColumnDef<TData, unknown>> & {
    accessorKey?: keyof TData;
    header: string;
  }
): ColumnDef<TData, unknown> {
  return {
    ...config,
    header: config.header,
    enableSorting: true,
  };
}

export function createSelectionColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    size: 40,
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(details) => {
          const checked = typeof details.checked === 'boolean' ? details.checked : false;
          table.toggleAllPageRowsSelected(checked);
        }}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(details) => {
          const checked = typeof details.checked === 'boolean' ? details.checked : false;
          row.toggleSelected(checked);
        }}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };
}

// Additional column helpers
export function createExpanderColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => {
      return row.getCanExpand() ? (
        <IconButton
          aria-label="Expand row"
          icon={row.getIsExpanded() ? <LuChevronDown /> : <LuChevronRight />}
          size="xs"
          variant="ghost"
          onClick={row.getToggleExpandedHandler()}
        />
      ) : null;
    },
    size: 40,
    enableSorting: false,
  };
}

export function createActionsColumn<TData>(
  actions: Array<{
    label: string;
    icon?: React.ReactElement;
    onClick: (row: Row<TData>) => void;
    isDisabled?: (row: Row<TData>) => boolean;
    isDestructive?: boolean;
  }>
): ColumnDef<TData, unknown> {
  return {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      // Temporary simple implementation to debug the issue
      return (
        <HStack gap={1}>
          {actions.map((action, index) => (
            <IconButton
              key={index}
              aria-label={action.label}
              icon={action.icon || <LuSettings />}
              size="xs"
              variant="ghost"
              onClick={() => action.onClick(row)}
              disabled={action.isDisabled?.(row)}
              colorPalette={action.isDestructive ? 'red' : undefined}
            />
          ))}
        </HStack>
      );
    },
    size: 50,
    enableSorting: false,
  };
}

export function createDateColumn<TData>(
  accessor: keyof TData | ((row: TData) => unknown),
  header: string,
  dateFormat?: Intl.DateTimeFormatOptions
): ColumnDef<TData, unknown> {
  return {
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header,
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return '-';
      const date = new Date(value as string | number | Date);
      return date.toLocaleDateString(undefined, dateFormat);
    },
    enableColumnFilter: true,
  };
}

export function createCurrencyColumn<TData>(
  accessor: keyof TData | ((row: TData) => unknown),
  header: string,
  currency = 'USD',
  locale = 'en-US'
): ColumnDef<TData, unknown> {
  return {
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header,
    cell: ({ getValue }) => {
      const value = getValue() as number;
      if (value == null) return '-';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(value);
    },
  };
}

export function createStatusColumn<TData>(
  accessor: keyof TData | ((row: TData) => unknown),
  header: string,
  statusConfig: Record<string, { label: string; color: string }>
): ColumnDef<TData, unknown> {
  return {
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header,
    cell: ({ getValue }) => {
      const value = getValue() as string;
      const config = statusConfig[value];
      if (!config) return value;

      return <Badge>{config.label}</Badge>;
    },
    filterFn: 'includesString',
  };
}

export function createGroupableColumn<TData>({
  accessor,
  header,
  enableGrouping = true,
  aggregationFn = 'count',
  aggregatedCell,
  ...rest
}: {
  accessor: keyof TData | ((row: TData) => unknown);
  header: string;
  enableGrouping?: boolean;
  aggregationFn?: 'count' | 'sum' | 'min' | 'max' | 'mean' | 'median' | 'unique' | 'uniqueCount';
  aggregatedCell?: (info: unknown) => React.ReactNode;
} & Partial<ColumnDef<TData, unknown>>): ColumnDef<TData, unknown> {
  return {
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header,
    enableGrouping,
    aggregationFn,
    aggregatedCell:
      aggregatedCell ||
      ((info) => {
        const value = info.getValue();
        if (aggregationFn === 'count') {
          return `${value} items`;
        }
        return value;
      }),
    ...rest,
  };
}

export function createResizableColumn<TData>(
  config: Partial<ColumnDef<TData, unknown>> & {
    accessorKey?: keyof TData;
    header: string;
    size?: number;
    minSize?: number;
    maxSize?: number;
  }
): ColumnDef<TData, unknown> {
  return {
    ...config,
    header: config.header,
    size: config.size || 150,
    minSize: config.minSize || 50,
    maxSize: config.maxSize || 500,
    enableResizing: true,
  };
}
